import React, { useRef, useState, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import { useGetPhonicsSoundsQuery } from '@/api/types';
import { useUi, useAssetCdn } from '@/context/Ui';
import { useLessonPlayer } from '@/context/LessonPlayer';
import { findSoundsInString } from '@/utils/sounds';
import { SymbolType, CdnObjectType } from '@/types';
import { AudioPlayer, CdnMedia, RoundPlayButton } from '@/components';
import { getRandomEntries, shuffleArray } from '@/utils/array';
import { LETTERS_WITH_DESCENDERS } from '@/utils/letters';
import { WhitePanel } from '../components/WhitePanel';
import { MultipleChoiceGame } from '../components/MultipleChoiceGame';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { Activity, ActivityStep } from '../Activity';
import { ActivityTypeProps } from '../types';

interface PlayNewSoundProps {
  sound: string;
}

const PlayNewSound = ({ sound }: PlayNewSoundProps) => {
  const playerRef = useRef<AudioPlayer>(null);

  const mediaUrl = useAssetCdn(sound, SymbolType.Sound, CdnObjectType.Audio);

  const [playedOnce, setPlayedOnce] = useState(false);

  return (
    <>
      <WhitePanel className={clsx('my-6')} mode="letter">
        <span
          className={clsx(
            LETTERS_WITH_DESCENDERS.includes(sound) && 'relative -top-8',
          )}
        >
          {sound}
        </span>
      </WhitePanel>

      <RoundPlayButton
        color="white"
        className="my-6"
        onClick={() => {
          playerRef.current?.play();
          setPlayedOnce(true);
        }}
        ariaLabel="Play sound"
        size="sm"
        pressHint={!playedOnce}
      />

      <AudioPlayer ref={playerRef} src={mediaUrl || ''} />
    </>
  );
};

interface ShowNewSoundProps {
  sound: string;
}

const ShowNewSound = ({ sound }: ShowNewSoundProps) => {
  const playerRef = useRef<AudioPlayer>(null);

  const { sendAlert } = useUi();

  const mediaUrl = useAssetCdn(
    sound,
    SymbolType.Sound,
    CdnObjectType.Audio,
    true,
  );

  const [playedOnce, setPlayedOnce] = useState(false);

  const [playerReady, setPlayerReady] = useState(false);

  return (
    <>
      <WhitePanel
        className="my-2 md:my-3 lg:my-2 md:w-[460px] md:h-[460px] lg:w-[440px] lg:h-[440px]"
        mode="letter"
        kind="bigBox"
      >
        <CdnMedia
          className="block mx-auto w-auto h-full"
          symbol={sound}
          symbolType={SymbolType.Sound}
          objectType={CdnObjectType.Image}
          width={200}
          height={200}
        />
      </WhitePanel>

      <RoundPlayButton
        color="white"
        className="my-2 md:my-3 lg:my-2"
        size="sm"
        onClick={() => {
          playerRef.current?.play();
          setPlayedOnce(true);
        }}
        ariaLabel="Play sound"
        disabled={!playerReady}
        pressHint={!playedOnce}
      />

      <AudioPlayer
        ref={playerRef}
        src={mediaUrl || ''}
        onReadySrc={() => setPlayerReady(true)}
        onLoadTimeoutSrc={() =>
          sendAlert('We could not load some of the audio for this lesson.', {
            type: 'warning',
            title: 'There was a problem',
          })
        }
      />
    </>
  );
};

interface PickNewSoundProps {
  sound: string;
  availableSounds: string[];
}

const DEFAULT_RANDOM_GRAPHEMES_COUNT = 8;

const PickNewSound = ({ sound, availableSounds }: PickNewSoundProps) => {
  const playerRef = useRef<AudioPlayer>(null);

  const mediaUrl = useAssetCdn(sound, SymbolType.Sound, CdnObjectType.Audio);

  const [playedOnce, setPlayedOnce] = useState(false);

  const [options, setOptions] = useState<string[]>([]);

  const [selectedLetters, setSelectedLetters] = useState<Array<string>>([]);

  useEffect(() => {
    // Build 9-choice grid: 8 random grapheme distractors + the correct grapheme
    // Never include single-letter 'q'; always use 'qu'
    const pool = Array.from(
      new Set(availableSounds.map((s) => (s === 'q' ? 'qu' : s))),
    );

    const distractors = getRandomEntries(pool, DEFAULT_RANDOM_GRAPHEMES_COUNT, [
      sound,
    ]);

    setOptions(shuffleArray([...distractors, sound]));
  }, [sound, availableSounds]);

  return (
    <>
      <RoundPlayButton
        color="white"
        className="my-6"
        onClick={() => {
          playerRef.current?.play();
          setPlayedOnce(true);
        }}
        ariaLabel="Play sound"
        size="sm"
        pressHint={!playedOnce}
      />

      <MultipleChoiceGame
        choices={options}
        correctChoice={sound}
        selectedChoices={selectedLetters}
        setSelectedChoices={setSelectedLetters}
      />

      <AudioPlayer ref={playerRef} src={mediaUrl || ''} />
    </>
  );
};

export const ReadingNewSounds = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  const { getLessonByActivity, getEncounteredLetters } = useLessonPlayer();

  if (!activity.sounds) {
    throw new Error('Sounds are required for this activity');
  }

  const { data: soundsData } = useGetPhonicsSoundsQuery();

  const availableSounds = useMemo(() => {
    const lesson = getLessonByActivity(activity);

    if (!lesson) {
      return;
    }

    return getEncounteredLetters(lesson.id);
  }, [activity, getEncounteredLetters, getLessonByActivity]);

  const sounds = useMemo(() => {
    if (!availableSounds) {
      return;
    }

    if (!activity.sounds) {
      return;
    }

    if (!soundsData) {
      return;
    }

    return findSoundsInString(availableSounds, activity.sounds);
  }, [activity, soundsData, availableSounds]);

  const NUMBER_OF_STEPS_PER_SOUND = 3;

  return (
    sounds && (
      <Activity
        steps={[
          <ActivityTitleStep
            key={`${activity.id}-title`}
            activity={activity}
            parrotType="squawking"
            mode={mode}
            wrapperClassName={wrapperClassName}
            isPreview={isPreview}
            index={0}
            total={sounds.length * NUMBER_OF_STEPS_PER_SOUND + 1}
          />,
          ...sounds?.flatMap((sound, index) => [
            <ActivityStep
              key={`${activity.id}-${index}-1`}
              stepIdentifier={`${activity.id}-${index}-1`}
              activity={activity}
              mode={mode}
              stepNumber={index * 1 + 1 + 1}
              totalSteps={sounds.length * NUMBER_OF_STEPS_PER_SOUND + 1}
              title={`Say the sound. Click the button to hear it.`}
              showTitle={true}
              showActivityPagination={false}
              wrapperClassName={wrapperClassName}
              bgOrnament
              isPreview={isPreview}
            >
              <PlayNewSound sound={sound} />
            </ActivityStep>,
            <ActivityStep
              key={`${activity.id}-${index}-2`}
              stepIdentifier={`${activity.id}-${index}-2`}
              activity={activity}
              mode={mode}
              stepNumber={index * 2 + 2 + 1}
              totalSteps={sounds.length * NUMBER_OF_STEPS_PER_SOUND + 1}
              title={`See the picture. Click to hear the sound in the word.`}
              showTitle={true}
              showActivityPagination={false}
              wrapperClassName={wrapperClassName}
              bgOrnament
              isPreview={isPreview}
              align="top"
            >
              <ShowNewSound sound={sound} />
            </ActivityStep>,
            <ActivityStep
              key={`${activity.id}-${index}-3`}
              stepIdentifier={`${activity.id}-${index}-3`}
              activity={activity}
              mode={mode}
              stepNumber={index * 3 + 3 + 1}
              totalSteps={sounds.length * NUMBER_OF_STEPS_PER_SOUND + 1}
              title={
                'Click the button. Match the letter sound to the letter shape.'
              }
              showTitle={true}
              showActivityPagination={false}
              wrapperClassName={wrapperClassName}
              bgOrnament
              isPreview={isPreview}
            >
              {availableSounds && (
                <PickNewSound sound={sound} availableSounds={availableSounds} />
              )}
            </ActivityStep>,
          ]),
        ]}
      />
    )
  );
};
